import { get } from "lodash";
import moment from "moment";
import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Col, Container } from "reactstrap";
import CollapsibleCard from "src/components/CollapsibleCard/CollapsibleCard";
import { ITableColumn } from "src/components/FinalTable/FinalTable";
import FormModal from "src/components/FormModalV2/FormModal";
import CustomIcon, { IconName } from "src/components/Icon/Icon";
import DisTable from "src/components/TableV2/Table";
import { IApplicationComment } from "src/store/models/application.model";
import {
  fetchBrokerCommentDetails,
  fetchBrokerCommentOptions,
  fetchBrokerCommentsList,
  submitBrokerComment,
} from "src/store/reducers/brokerComments";
import Toast from "../../../components/Toast/Toast";
import * as S from "../../../constants/StringConstants";
import { IRootState } from "../../../store/reducers";
import { CommentForm } from "../../BrokerBuilder/BrokerComments/CommentsForm";
import "./BrokerCommentsWidget.scss";
import { columns } from "./CommentsColumns/CommentsColumns";

interface RouteParams {
  id: string;
}

const BrokerCommentsWidget: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [newComment, setNewComment] = useState<string>("");
  const fileTableRef = useRef<any>(null);
  const { id: brokerID } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const { userID, brokerCommentDetails } = useSelector((state: IRootState) => ({
    userID: state.auth.user.userID,
    brokerCommentDetails: state.brokerComments.commentDetails,
  }));

  const widgetColumns: ITableColumn<IApplicationComment>[] = columns
    .filter(column => column.key !== "confidential")
    .map(column => {
      if (column.key === "createdDateTime") {
        return {
          ...column,
          title: S.APP_COMMENTS_COL_DATE,
          render: (item: IApplicationComment) => moment(item.createdDateTime).format("MM/DD/YYYY"),
        };
      }
      return column;
    });

  if (brokerID === "new") {
    return null;
  }

  const initialValues = {
    id: brokerCommentDetails?.id,
    dueDate: brokerCommentDetails?.dueDate,
    followUpDate: brokerCommentDetails?.followUpDate,
    confidentialFlag: brokerCommentDetails?.confidentialFlag,
    reoccurFlag: brokerCommentDetails?.reoccurFlag,
    description: brokerCommentDetails?.description,
    commentPriorityID: get(brokerCommentDetails, "commentPriorityID"),
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);

    const payload = {
      ...values,
      comment: newComment.trim() ? newComment : undefined,
    };

    try {
      await dispatch(submitBrokerComment(brokerID, userID, payload));
      if (fileTableRef.current) {
        fileTableRef.current.update({});
      }
      setIsFormOpen(false);
      setNewComment("");
    } catch (error) {
      console.error("Failed to submit the comment:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CollapsibleCard title={S.PD_BROKER_COMMENTS_HEADER} iconName={IconName.BrokerBuilderComments} defaultOpen={true}>
        <Container>
          <div className="broker-comments-table">
            <DisTable<IApplicationComment>
              getRef={ft => {
                fileTableRef.current = ft;
              }}
              columns={widgetColumns}
              isPaginated={true}
              hidePaginationInfo={true}
              initialState={{}}
              onRowClick={async (data: IApplicationComment) => {
                setLoading(true);
                await dispatch(fetchBrokerCommentDetails(data.id));
                setLoading(false);
                setIsFormOpen(true);
              }}
              onUpdate={async ({ page, rows, sort, query }) => {
                setLoading(true);
                const result: any = await dispatch(fetchBrokerCommentsList({ brokerID, page, rows, sort, query }));
                setLoading(false);
                return {
                  total: get(result, "value.data.totalRows") as number,
                  data: get(result, "value.data.items") as IApplicationComment[],
                };
              }}
              noResultsMessage={<p className="no-comments-message">{S.APP_BROKER_NO_COMMENTS_AVAILABLE}</p>}
            />
          </div>

          <div className="add-comments-button">
            <button
              type="button"
              className="button-v2__medication-link-blue large"
              onClick={() => {
                dispatch(fetchBrokerCommentOptions());
                setIsFormOpen(true);
              }}>
              <span className="add-comments-button__label">
                {S.BRO_COMMENTS_ADD_LABEL}
                <CustomIcon name={IconName.AddPlusCircleBlue} size="20px" />
              </span>
            </button>
          </div>

          <FormModal
            open={isFormOpen}
            onCancel={() => setIsFormOpen(false)}
            values={initialValues}
            onSubmit={handleSubmit}>
            <Col sm="12">
              <CommentForm
                priorityList={brokerCommentDetails?.selectPriorityList}
                newComment={newComment}
                setNewComment={setNewComment}
              />
            </Col>
          </FormModal>
        </Container>
      </CollapsibleCard>

      <Toast open={loading} message={S.LOADING} onClose={() => {}} />
    </>
  );
};

export default BrokerCommentsWidget;
